import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, isToday } from 'date-fns';
import type { ScheduledPostEntry } from '../../types/calendar';

interface CalendarDayTimelineProps {
  /** The date to display */
  date: Date;
  /** Posts scheduled for this day */
  posts: ScheduledPostEntry[];
  /** Handler for clicking on a post */
  onPostClick?: (post: ScheduledPostEntry) => void;
  /** Handler for clicking on a time slot to add a post */
  onAddPost?: (date: Date, hour: number) => void;
  /** Handler for rescheduling a post */
  onReschedulePost?: (post: ScheduledPostEntry) => void;
  /** Handler for unscheduling a post */
  onUnschedulePost?: (post: ScheduledPostEntry) => void;
}

/**
 * CalendarDayTimeline component
 * Displays a 24-hour timeline view with drag-and-drop support (15-minute precision).
 */
export default function CalendarDayTimeline({
  date,
  posts,
  onPostClick,
  onAddPost,
  onReschedulePost,
  onUnschedulePost,
}: CalendarDayTimelineProps) {
  const { t } = useTranslation();
  const today = isToday(date);

  // Drag state
  const [draggedPost, setDraggedPost] = useState<ScheduledPostEntry | null>(null);
  const [dropTargetSlot, setDropTargetSlot] = useState<{ hour: number; minute: number } | null>(null);

  /**
   * Generate 24 hours (0-23)
   */
  const hours = useMemo(() => {
    return Array.from({ length: 24 }, (_, i) => i);
  }, []);

  /**
   * Generate 15-minute slots per hour (0, 15, 30, 45)
   */
  const minuteSlots = useMemo(() => [0, 15, 30, 45], []);

  /**
   * Group posts by time slot (hour + 15-min bucket)
   */
  const postsBySlot = useMemo(() => {
    const grouped: Record<string, ScheduledPostEntry[]> = {};

    posts.forEach((post) => {
      const postDate = new Date(post.scheduledAt);
      const hour = postDate.getHours();
      const minute = postDate.getMinutes();
      // Round down to nearest 15-minute slot
      const slotMinute = Math.floor(minute / 15) * 15;
      const key = `${hour}-${slotMinute}`;

      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(post);
    });

    return grouped;
  }, [posts]);

  /**
   * Format hour for display
   */
  const formatHour = (hour: number): string => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  /**
   * Get current hour/minute for highlighting
   */
  const currentTime = useMemo(() => {
    if (!today) return null;
    const now = new Date();
    return {
      hour: now.getHours(),
      minute: Math.floor(now.getMinutes() / 15) * 15,
    };
  }, [today]);

  /**
   * Handle drag start
   */
  const handleDragStart = (e: React.DragEvent, post: ScheduledPostEntry) => {
    setDraggedPost(post);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', post.scheduleId);

    // Add ghost image
    if (e.currentTarget instanceof HTMLElement) {
      const ghost = e.currentTarget.cloneNode(true) as HTMLElement;
      ghost.style.opacity = '0.5';
      ghost.style.position = 'absolute';
      ghost.style.top = '-1000px';
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 0, 0);
      setTimeout(() => document.body.removeChild(ghost), 0);
    }
  };

  /**
   * Handle drag over slot (for drop zone highlighting)
   */
  const handleDragOver = (e: React.DragEvent, hour: number, minute: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetSlot({ hour, minute });
  };

  /**
   * Handle drag leave
   */
  const handleDragLeave = () => {
    setDropTargetSlot(null);
  };

  /**
   * Handle drop
   */
  const handleDrop = (e: React.DragEvent, hour: number, minute: number) => {
    e.preventDefault();

    if (draggedPost && onReschedulePost) {
      // Create new scheduled date with the dropped time
      const newDate = new Date(date);
      newDate.setHours(hour, minute, 0, 0);

      // Update the post with new time
      const updatedPost: ScheduledPostEntry = {
        ...draggedPost,
        scheduledAt: newDate.toISOString(),
      };

      onReschedulePost(updatedPost);
    }

    setDraggedPost(null);
    setDropTargetSlot(null);
  };

  /**
   * Handle drag end
   */
  const handleDragEnd = () => {
    setDraggedPost(null);
    setDropTargetSlot(null);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Timeline container */}
      <div className="flex-1 overflow-y-auto border-2 border-black dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
        <div className="relative">
          {/* Hour rows */}
          {hours.map((hour) => (
            <div key={hour} className="flex border-b border-gray-200 dark:border-gray-700">
              {/* Time label (spans all 4 15-min slots) */}
              <div className="w-20 flex-shrink-0 p-2 text-right border-r border-gray-200 dark:border-gray-700">
                <span className={`
                  text-sm font-medium
                  ${currentTime?.hour === hour
                    ? 'text-yellow-600 dark:text-yellow-400 font-bold'
                    : 'text-gray-600 dark:text-gray-400'
                  }
                `}>
                  {formatHour(hour)}
                </span>
              </div>

              {/* 15-minute slots */}
              <div className="flex-1 flex flex-col">
                {minuteSlots.map((minute) => {
                  const slotKey = `${hour}-${minute}`;
                  const slotPosts = postsBySlot[slotKey] || [];
                  const isCurrentSlot = currentTime?.hour === hour && currentTime?.minute === minute;
                  const isDropTarget = dropTargetSlot?.hour === hour && dropTargetSlot?.minute === minute;

                  return (
                    <div
                      key={slotKey}
                      className={`
                        relative min-h-[60px] p-1
                        border-t border-gray-100 dark:border-gray-700/50
                        ${minute === 0 ? 'border-t-2' : ''}
                        ${isCurrentSlot ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}
                        ${isDropTarget ? 'bg-blue-100 dark:bg-blue-900/30 ring-2 ring-blue-400 ring-inset' : ''}
                        transition-colors
                      `}
                      onDragOver={(e) => handleDragOver(e, hour, minute)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, hour, minute)}
                    >
                      {/* Minute marker (subtle) */}
                      {minute !== 0 && (
                        <div className="absolute left-0 top-0 text-[10px] text-gray-400 dark:text-gray-600 pl-1">
                          :{minute.toString().padStart(2, '0')}
                        </div>
                      )}

                      {slotPosts.length === 0 ? (
                        // Empty slot - clickable
                        <button
                          onClick={() => onAddPost?.(date, hour)}
                          className="
                            w-full h-full min-h-[50px]
                            flex items-center justify-center
                            text-gray-300 dark:text-gray-600
                            hover:text-gray-500 dark:hover:text-gray-400
                            hover:bg-gray-50 dark:hover:bg-gray-700/30
                            rounded
                            transition-all
                            opacity-0 hover:opacity-100
                          "
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                        </button>
                      ) : (
                        // Display posts
                        <div className="space-y-1">
                          {slotPosts.map((post) => {
                            const isDragging = draggedPost?.scheduleId === post.scheduleId;

                            return (
                              <div
                                key={post.scheduleId}
                                draggable
                                onDragStart={(e) => handleDragStart(e, post)}
                                onDragEnd={handleDragEnd}
                                onClick={() => onPostClick?.(post)}
                                className={`
                                  relative p-2 rounded-lg
                                  border-2 border-black dark:border-gray-600
                                  bg-white dark:bg-gray-800
                                  hover:shadow-brutal
                                  transition-all duration-150
                                  cursor-move
                                  group
                                  ${isDragging ? 'opacity-40' : 'opacity-100'}
                                `}
                              >
                                {/* Drag handle indicator */}
                                <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-50">
                                  <svg
                                    className="w-3 h-3 text-gray-400"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V4a2 2 0 00-2-2H7zm3 14a1 1 0 100-2 1 1 0 000 2zm0-4a1 1 0 100-2 1 1 0 000 2zm0-4a1 1 0 100-2 1 1 0 000 2z" />
                                  </svg>
                                </div>

                                {/* Time and status */}
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-bold text-gray-900 dark:text-gray-100 ml-4">
                                    {format(new Date(post.scheduledAt), 'HH:mm')}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    {/* Auto-publish indicator */}
                                    {post.autoPublish && (
                                      <span
                                        className="text-gray-500 dark:text-gray-400"
                                        title={t('calendar.post.autoPublish')}
                                      >
                                        <svg
                                          className="w-3 h-3"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                          />
                                        </svg>
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Title */}
                                <p className="text-xs text-gray-900 dark:text-gray-100 font-medium mb-1 ml-4">
                                  {post.post.title || t('calendar.post.untitled')}
                                </p>

                                {/* Platforms */}
                                <div className="flex flex-wrap gap-1 ml-4">
                                  {post.post.platforms.map((platform) => (
                                    <span
                                      key={platform}
                                      className="
                                        text-[9px] px-1.5 py-0.5 rounded
                                        font-bold uppercase tracking-wider
                                        text-white
                                      "
                                      style={{
                                        backgroundColor:
                                          platform === 'linkedin' ? '#0077b5' :
                                          platform === 'instagram' ? '#E1306C' :
                                          platform === 'facebook' ? '#1877f2' :
                                          platform === 'tiktok' ? '#000000' :
                                          platform === 'youtube' ? '#FF0000' :
                                          platform === 'twitter' ? '#1da1f2' : '#666666'
                                      }}
                                    >
                                      {platform}
                                    </span>
                                  ))}
                                </div>

                                {/* Recurring/Conflict indicators */}
                                <div className="flex items-center gap-2 mt-1 ml-4">
                                  {post.isRecurring && (
                                    <div className="flex items-center gap-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                                      <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                      </svg>
                                    </div>
                                  )}
                                  {post.conflictWarning && (
                                    <div className="flex items-center gap-0.5 text-[10px] text-amber-600 dark:text-amber-400">
                                      <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Drop instruction */}
      {draggedPost && (
        <div className="mt-2 p-2 text-center text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
          Sleep de post naar een tijdslot om te verplaatsen (15-minuten precisie)
        </div>
      )}

      {/* Empty state if no posts at all */}
      {posts.length === 0 && !draggedPost && (
        <div className="mt-4 p-4 text-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400">
            {t('calendar.empty.dayDescription')}
          </p>
          <button
            onClick={() => onAddPost?.(date, new Date().getHours())}
            className="
              mt-2 px-4 py-2
              bg-black dark:bg-white
              text-white dark:text-black
              font-medium rounded-lg
              border-2 border-black dark:border-white
              hover:shadow-brutal
              transition-shadow
            "
          >
            {t('calendar.actions.schedulePost')}
          </button>
        </div>
      )}
    </div>
  );
}
